import { parse, stringify } from "yaml";

import { BuilderNode } from "../../utils/conditions";

/**
 * A row of the marketing_campaign table (gladstone-data-retriever). `content` is
 * the campaign definition as a YAML string (see CampaignConfig for its shape).
 * This is the admin/CRUD view — distinct from the participant-facing
 * MarketingCampaign ({ name, paused }) model used for campaign pause status.
 */
export interface MarketingCampaignAdmin {
	id: number | null;
	name: string;
	content: string;
	deleted?: boolean;
	createdOn?: string;
	updatedOn?: string;
}

/**
 * A single scheduled message inside a campaign. Mirrors `RawMessageConf` in
 * product/marketing (potsy/config_parser.py). `template` links to a Message
 * Template by name; `ref_template` is the (optional) Mustache ref-key override.
 * The index signature preserves any field we don't explicitly model on save.
 */
export interface CampaignMessage {
	name: string;
	channel: string;
	offset: string;
	relative_to: string;
	template: string;
	subject?: string | null;
	preview_text?: string | null;
	sender?: string | null;
	ref_template?: string | null;
	max_offset?: string | null;
	message_date?: string | null;
	limit?: number | null;
	conditions?: unknown;
	// UI-only fields (prefixed `__`, all stripped on save):
	// stable id for collapse state, the conditions builder model, the raw YAML
	// fallback text, and whether that editor is in advanced (YAML) mode.
	__uiId?: string;
	__conditionsModel?: BuilderNode | null;
	__conditionsText?: string;
	__conditionsAdvanced?: boolean;
	[key: string]: unknown;
}

/** The parsed campaign YAML. Mirrors `Config` in product/marketing. */
export interface CampaignConfig {
	campaign?: string;
	campaign_launch?: string;
	ref_template?: string;
	conditions?: unknown;
	messages: CampaignMessage[];
	[key: string]: unknown;
}

/** Parse a campaign's YAML content into a structured config (never throws). */
export const parseCampaignContent = (content: string): CampaignConfig => {
	try {
		const parsed = (content ? parse(content) : {}) as Partial<CampaignConfig> | null;
		const config = (parsed ?? {}) as CampaignConfig;
		if (!Array.isArray(config.messages)) config.messages = [];
		return config;
	} catch {
		return { messages: [] };
	}
};

/** Serialize a structured config back to YAML for storage. */
export const serializeCampaignContent = (config: CampaignConfig): string =>
	stringify(config, { lineWidth: 0 });

export const emptyCampaignMessage = (): CampaignMessage => ({
	name: "",
	channel: "marketing_email",
	offset: "0d",
	relative_to: "campaign_launch",
	template: "",
});

export const emptyCampaign = (): MarketingCampaignAdmin => ({
	id: null,
	name: "",
	content: serializeCampaignContent({
		campaign: "",
		campaign_launch: "",
		ref_template: "{{campaign}}.{{name}}",
		conditions: [],
		messages: [],
	}),
});

/** Delivery channels supported by the campaign sender (potsy `client_lookup`). */
export const CAMPAIGN_CHANNELS = ["marketing_email", "coach_email", "coach_sms", "push"];

/** Human-friendly labels for the known delivery channels. */
const CHANNEL_LABELS: Record<string, string> = {
	marketing_email: "Marketing email",
	coach_email: "Coach email",
	coach_sms: "Coach SMS",
	push: "Push notification",
};

/** Pretty label for a channel value; falls back to Title Case of the raw key. */
export const formatChannelLabel = (channel: string): string =>
	CHANNEL_LABELS[channel] ??
	channel
		.split("_")
		.map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
		.join(" ");
